import * as React from "react";
import { RouteComponentProps, withRouter } from "react-router";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Send, User, Eye, ThumbsUp, MessageSquare, Share2, MousePointerClick } from "lucide-react";

import { Skeleton } from "@/components/ui";
import { HealthCard, HealthInfo, HealthStat } from "../../../../Coach/Dashboard/components/HealthCard";
import { EntityType, LeaderboardMetric, PlayerStatType, StatsServices } from "@/services/bedrock";
import { useAccountSettings } from "@/utils";
import { LeaderboardModal } from "@/components/LeaderboardModal";

interface OwnProps {
  coachId: number;
  playerId: number;
}
type Props = OwnProps & RouteComponentProps;

const StatisticsFC: React.FC<Props> = ({ coachId, playerId }) => {
  const [showPostLeaderboard, setShowPostLeaderboard] = React.useState<boolean>(false);
  const [leaderboardMetric, setLeaderboardMetric] = React.useState<LeaderboardMetric>("total_reactions");
  const [entityType, setEntityType] = React.useState<EntityType>("post");

  const { hasAccountSetting } = useAccountSettings();

  const { data: stats, isLoading: loading } = useQuery({
    queryKey: ["playerStats", playerId],
    queryFn: () => StatsServices.getPlayerStats(),
    enabled: !!playerId,
  });

  const getStatInfo = (type: PlayerStatType): HealthStat | undefined => {
    const stat = stats?.statistics?.[type];
    if (stat === undefined) {
      return undefined;
    }
    const percentValue = Number(stat.percent);
    const color = percentValue === 0 ? undefined : percentValue > 0 ? "text-green-600" : "text-red-600";
    return {
      value: stat.value,
      label: `${percentValue >= 0 ? "+" : "-"}${Math.abs(percentValue)}%`,
      labelColor: color,
    };
  };

  const playerHealthStats: HealthInfo[] = useMemo(() => {
    if (!stats?.statistics) return [];

    return [
      {
        key: "posts",
        title: "Posts",
        primaryValue: getStatInfo("GOALS_POSTED"),
        secondaryValues: [],
        footer: "Last 30 days",
        icon: Send,
        onClick: () => {
          setEntityType("post");
          setShowPostLeaderboard(true);
        },
      },
      {
        key: "followers",
        title: "Followers",
        primaryValue: getStatInfo("FOLLOWERS"),
        secondaryValues: [],
        icon: User,
        onClick: () => {
          setEntityType("social_identity");
          setLeaderboardMetric("follower_change");
          setShowPostLeaderboard(true);
        },
      },
      {
        key: "impressions",
        title: "Impressions",
        primaryValue: getStatInfo("IMPRESSIONS"),
        secondaryValues: [],
        footer: "Last 30 days",
        icon: Eye,
        onClick: () => {
          setEntityType("post");
          setLeaderboardMetric("impressions");
          setShowPostLeaderboard(true);
        },
      },
      {
        key: "reactions",
        title: "Reactions",
        primaryValue: getStatInfo("REACTIONS"),
        secondaryValues: [],
        footer: "Last 30 days",
        icon: ThumbsUp,
        onClick: () => {
          setEntityType("post");
          setLeaderboardMetric("total_reactions");
          setShowPostLeaderboard(true);
        },
      },
      {
        key: "comments",
        title: "Comments",
        primaryValue: getStatInfo("COMMENTS"),
        secondaryValues: [],
        footer: "Last 30 days",
        icon: MessageSquare,
        onClick: () => {
          setEntityType("post");
          setLeaderboardMetric("comment_count");
          setShowPostLeaderboard(true);
        },
      },
      {
        key: "shares",
        title: "Shares",
        primaryValue: getStatInfo("SHARES"),
        secondaryValues: [],
        footer: "Last 30 days",
        icon: Share2,
        onClick: () => {
          setEntityType("post");
          setLeaderboardMetric("share_count");
          setShowPostLeaderboard(true);
        },
      },
      {
        key: "clicks",
        title: "Clicks",
        primaryValue: getStatInfo("CLICKS"),
        secondaryValues: [],
        footer: "Last 30 days",
        icon: MousePointerClick,
        onClick: () => {
          setEntityType("post");
          setLeaderboardMetric("click_count");
          setShowPostLeaderboard(true);
        },
      },
    ] as HealthInfo[];
  }, [setShowPostLeaderboard, setLeaderboardMetric, setEntityType, stats?.statistics]);

  return (
    <div className="statisticsContent w-full">
      <div className="statistics flex flex-col gap-2 w-full">
        <div className="text-2xl font-bold text-slate-700 leading-snug">Social Insights</div>

        <div className="flex flex-row flex-wrap gap-4">
          {loading
            ? Array.from({ length: 7 }).map((_, idx) => (
                <div
                  key={`skeleton-${idx}`}
                  className="bg-white px-4 py-3.5 rounded-lg border border-slate-200/50 w-[150px] h-[115px] flex flex-col justify-between animate-pulse select-none"
                >
                  <div className="flex items-center gap-1.5">
                    <Skeleton className="size-4 rounded-md" />
                    <Skeleton className="h-4 w-16" />
                  </div>
                  <div className="flex items-baseline justify-between mt-2">
                    <Skeleton className="h-8 w-12" />
                    <Skeleton className="h-4 w-8" />
                  </div>
                  <div className="mt-1">
                    <Skeleton className="h-3 w-16" />
                  </div>
                </div>
              ))
            : playerHealthStats.map(info => <HealthCard key={info.key} info={info} />)}
        </div>
      </div>

      <LeaderboardModal
        open={showPostLeaderboard}
        onClose={() => setShowPostLeaderboard(false)}
        board={{
          title: `Top ${entityType === "post" ? "Posts" : "Accounts"}`,
          entityType: entityType,
          metric: leaderboardMetric,
          startTimeKey: "30d",
        }}
      />
    </div>
  );
};

export const Statistics = withRouter(StatisticsFC);
